import {memo} from 'react';

import styled, {css, useTheme} from 'styled-components';

import {getLocalizationService} from '~/shared/services/localisationService';
import {flipOnLTR} from '~/shared/theme/utils';
import {formatAddress} from '~/shared/services/addressHelper';
import ImageWithAlt from '~/shared/components/ImageWithAlt';
import autoRenewIconUrl from '~/assets/images/ic_autorenew.png';
import loadingIconUrl from '~/assets/images/icons/spinner.svg';
import {addressKeyFromParts} from '~/shared/utils/address';

import Address from './Address';

const DirectionIcon = memo(({color}) => {
  const theme = useTheme();

  return (
    <svg width="17" height="18" xmlns="http://www.w3.org/2000/svg">
      <path
        stroke={color || theme.colors.secondary}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15.753 1.147L1 7.607l6.212 2.08 2.273 6.405z"
        fill="none"
        fillRule="evenodd"
      />
    </svg>
  );
});

const List = styled.div.attrs({
  'data-test-id': 'addressesList',
})`
  flex: 1;
  max-height: 460px;
`;

const DirectionIconWrapper = styled.div`
  display: flex;
  ${flipOnLTR`
    margin-left: 8px;
  `}
`;

const spin = css`
  @keyframes spin {
    from {
      transform: rotate3d(0, 0, 1, 0deg);
    }

    to {
      transform: rotate3d(0, 0, 1, 360deg);
    }
  }

  animation-name: spin;
  animation-duration: 2s;
  animation-iteration-count: infinite;
  animation-timing-function: linear;
`;

const Spinner = styled(ImageWithAlt).attrs({
  src: loadingIconUrl,
  noAlt: true,
})`
  width: 24px;
  height: 24px;
  ${spin}
`;

const RenewIcon = styled(ImageWithAlt).attrs({
  src: autoRenewIconUrl,
  noAlt: true,
})`
  width: 35px;
  height: 35px;
  transform: translateY(-5px);
`;

const AddressList = ({
  hideCurrentLocation,
  myLocationAddress,
  onMyLocationAddressChange,
  getCurrentAddress,
  hideEditButton,
  onAddressEditClick,
  locationAccessEnabled,
  addresses,
  onAddressChange,
  activeAddress,
}) => {
  const {t, currentLanguageKey} = getLocalizationService();

  return (
    <List>
      {!hideCurrentLocation && (
        <Address
          dataTestId="locateMyAddressBtn"
          label={t('current_address')}
          onClick={() =>
            (myLocationAddress.data ? onMyLocationAddressChange(myLocationAddress.data) : getCurrentAddress())}
          onEdit={hideEditButton ? null : myLocationAddress.data && (() => onAddressEditClick(myLocationAddress.data))}
          isSelected={false}
          value={(
            <>
              <DirectionIconWrapper>
                <DirectionIcon noAlt />
              </DirectionIconWrapper>
              <span data-test-id="locateMyAddressText">
                {locationAccessEnabled
                  ? myLocationAddress.error ||
                  (myLocationAddress.data
                    ? formatAddress({address: myLocationAddress.data, lng: currentLanguageKey})
                    : t(myLocationAddress.loading ? 'determining' : 'use_my_current_location'))
                  : t('location_access_denied')}
              </span>
            </>
          )}
          themekey="currentAddress"
          displayIconOnHover={1}
          isSingleLine
        >
          {myLocationAddress.loading ? (
            <Spinner />
          ) : (
            myLocationAddress.error && locationAccessEnabled && <RenewIcon onClick={getCurrentAddress} />
          )}
        </Address>
      )}

      {addresses &&
        addresses.map(address => (
          <Address
            key={`${address.addressId > 0 ? address.addressId : addressKeyFromParts(address)}`}
            onClick={() => onAddressChange(address)}
            isSelected={activeAddress === address}
            onEdit={hideEditButton ? null : () => onAddressEditClick(address)}
            label={t(address.isCompanyAddress ? 'office' : 'private_address')}
            value={formatAddress({address, lng: currentLanguageKey})}
            themekey={activeAddress === address ? 'selected' : 'address'}
            hideCurrentLocation={hideCurrentLocation}
          />
        ))}
    </List>
  );
};

export default memo(AddressList);
